import {
  ChangeDetectionStrategy, Component, computed, effect, ElementRef, input,
  output, signal, viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { HlmIconComponent } from '../../ui';

export interface SelectableEntity {
  id: number;
  status?: string;
}

@Component({
  selector: 'cmt-entity-selector',
  standalone: true,
  imports: [FormsModule, CdkConnectedOverlay, CdkOverlayOrigin, HlmIconComponent],
  templateUrl: './entity-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntitySelectorComponent {
  entities = input<SelectableEntity[]>([]);
  selectedEntity = input<SelectableEntity | null>(null);
  label = input<string>('');
  icon = input<string>('lucideUserRound');
  displayFn = input<(entity: SelectableEntity) => string>((e) => `${e.id}`);
  nameFn = input<(entity: SelectableEntity) => string>((e) => `${e.id}`);
  uaIdFn = input<(entity: SelectableEntity) => string>((e) => `${e.id}`);
  psIdFn = input<(entity: SelectableEntity) => string>((e) => '—');
  statusFn = input<(entity: SelectableEntity) => string>((e) => e.status ?? '');

  entitySelected = output<SelectableEntity>();

  // Optional integrated footer action row (rendered inside the selector card).
  footerLabel = input<string | null>();
  footerIcon = input<string>('lucidePhone');
  footerAction = output<void>();

  isOpen = signal(false);
  filterText = signal('');
  highlightedIndex = signal<number>(0);

  readonly filterInput = viewChild<ElementRef<HTMLInputElement>>('filterInput');
  readonly listRef = viewChild<ElementRef<HTMLElement>>('listRef');

  private readonly entityViewModels = computed(() => {
    const nameFn = this.nameFn();
    const uaIdFn = this.uaIdFn();
    const psIdFn = this.psIdFn();
    const statusFn = this.statusFn();
    const displayFn = this.displayFn();
    return this.entities().map(e => ({
      entity: e,
      name: nameFn(e),
      uaId: uaIdFn(e),
      psId: psIdFn(e),
      status: statusFn(e),
      display: displayFn(e),
    }));
  });

  filteredEntities = computed(() => {
    const filter = this.filterText().toLowerCase();
    const vms = this.entityViewModels();
    return filter ? vms.filter(vm => vm.display.toLowerCase().includes(filter)) : vms;
  });

  readonly selectedEntityVm = computed(() => {
    const sel = this.selectedEntity();
    if (!sel) return null;
    return {
      name: this.nameFn()(sel),
      uaId: this.uaIdFn()(sel),
      psId: this.psIdFn()(sel),
    };
  });

  readonly copiedField = signal<string | null>(null);
  private copyTimeout: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    effect(() => {
      const len = this.filteredEntities().length;
      const idx = this.highlightedIndex();
      if (len === 0) {
        if (idx !== -1) this.highlightedIndex.set(-1);
      } else if (idx < 0 || idx >= len) {
        this.highlightedIndex.set(0);
      }
    });
  }

  toggle(): void {
    this.isOpen.update(v => !v);
    if (this.isOpen()) {
      this.filterText.set('');
      const selected = this.selectedEntity();
      const list = this.filteredEntities();
      const selIdx = selected ? list.findIndex(vm => vm.entity.id === selected.id) : -1;
      this.highlightedIndex.set(selIdx >= 0 ? selIdx : (list.length ? 0 : -1));
      setTimeout(() => {
        this.filterInput()?.nativeElement.focus();
        this.scrollHighlightedIntoView();
      }, 0);
    }
  }

  close(): void { this.isOpen.set(false); }

  copyId(event: Event, field: 'ua' | 'ps'): void {
    event.stopPropagation();
    const entity = this.selectedEntity();
    if (!entity) return;
    const id = field === 'ua' ? this.uaIdFn()(entity) : this.psIdFn()(entity);
    navigator.clipboard.writeText(id);
    clearTimeout(this.copyTimeout);
    this.copiedField.set(field);
    this.copyTimeout = setTimeout(() => this.copiedField.set(null), 1500);
  }

  select(entity: SelectableEntity): void {
    this.entitySelected.emit(entity);
    this.close();
  }

  onKeydown(event: KeyboardEvent): void {
    const list = this.filteredEntities();
    const len = list.length;
    switch (event.key) {
      case 'ArrowDown':
        if (!len) return;
        event.preventDefault();
        this.highlightedIndex.set((this.highlightedIndex() + 1) % len);
        this.scrollHighlightedIntoView();
        break;
      case 'ArrowUp':
        if (!len) return;
        event.preventDefault();
        this.highlightedIndex.set((this.highlightedIndex() - 1 + len) % len);
        this.scrollHighlightedIntoView();
        break;
      case 'Home':
        if (!len) return;
        event.preventDefault();
        this.highlightedIndex.set(0);
        this.scrollHighlightedIntoView();
        break;
      case 'End':
        if (!len) return;
        event.preventDefault();
        this.highlightedIndex.set(len - 1);
        this.scrollHighlightedIntoView();
        break;
      case 'PageDown':
        if (!len) return;
        event.preventDefault();
        this.highlightedIndex.set(Math.min(len - 1, this.highlightedIndex() + 5));
        this.scrollHighlightedIntoView();
        break;
      case 'PageUp':
        if (!len) return;
        event.preventDefault();
        this.highlightedIndex.set(Math.max(0, this.highlightedIndex() - 5));
        this.scrollHighlightedIntoView();
        break;
      case 'Enter': {
        event.preventDefault();
        const idx = this.highlightedIndex();
        if (idx >= 0 && idx < len) this.select(list[idx].entity);
        break;
      }
    }
  }

  onFilterChange(value: string): void {
    this.filterText.set(value);
    this.highlightedIndex.set(this.filteredEntities().length ? 0 : -1);
  }

  isDeactivated(status: string): boolean {
    return status === 'deaktiv' || status === 'deactivated';
  }

  private scrollHighlightedIntoView(): void {
    setTimeout(() => {
      const host = this.listRef()?.nativeElement;
      if (!host) return;
      const item = host.querySelector<HTMLElement>('[data-highlighted="true"]');
      item?.scrollIntoView({ block: 'nearest' });
    }, 0);
  }
}
